import React, { useState, useRef } from 'react';
import RankingChart from '../components/charts/RankingChart';
import { AnalysisResults } from '../types';

interface DashboardProps {
  results?: AnalysisResults;
  loading?: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ results, loading }) => {
  // const { theme } = useTheme(); // Temporarily removed as theme is handled by CSS variables

  // Sort rankings by score descending to match the chart order
  const sortedRankings = results ? [...results.rankings].sort((a, b) => b.score - a.score) : [];

  // State for highlighting selected candidate
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);

  // Refs for scrolling
  const chartRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const candidateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Handle clicking on a chart bar to scroll to candidate details
  const handleChartBarClick = (ranking: any) => {
    setSelectedCandidate(ranking);
    // Scroll to the specific candidate card so the name is visible
    setTimeout(() => {
      const candidateElement = candidateRefs.current[ranking.resumeFilename];
      if (candidateElement) {
        candidateElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'  // Center the candidate card in viewport so name is visible
        });
      }
    }, 100);
  };

  // Handle clicking on candidate name to scroll back to chart
  const handleCandidateNameClick = (ranking: any) => {
    console.log('Candidate clicked:', ranking.resumeFilename);
    console.log('Current selectedCandidate:', selectedCandidate?.resumeFilename);
    setSelectedCandidate(ranking);
    // Scroll back to the chart
    setTimeout(() => {
      chartRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Analyzing resumes...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="dashboard empty">
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>Welcome to your Dashboard</h2>
          <p>Upload resumes and provide a job description to see ranking results here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Dashboard Header with Title and Icon */}
      <div className="dashboard-header">
        <div className="dashboard-title">
          <span className="dashboard-icon">📋</span>
          <h1>Ranking Dashboard</h1>
        </div>
      </div>

      {/* Candidate Match Score Section */}
      <div className="dashboard-content">
        <div className="match-score-card">
          <div className="card-header">
            <span className="card-icon">⭐</span>
            <h2>Candidate Match Score</h2>
          </div>
          <p className="card-description">
            Overall match score for each candidate based on the job description.
            Click on any bar to scroll to their detailed analysis below.
          </p>

          {/* Chart Section */}
          {results.rankings && results.rankings.length > 0 && (
            <div ref={chartRef} className="chart-container">
              <RankingChart
                rankings={sortedRankings}
                onBarClick={handleChartBarClick}
                selectedCandidate={selectedCandidate}
              />
            </div>
          )}
        </div>

        {/* Statistics Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <span className="stat-value">{results.rankings?.length || 0}</span>
            <span className="stat-label">Resumes Analyzed</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {results.rankings?.[0]?.score?.toFixed(1) || 'N/A'}%
            </span>
            <span className="stat-label">Top Score</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {Math.round((results.rankings?.reduce((sum: number, r: any) => sum + r.score, 0) || 0) / (results.rankings?.length || 1))}%
            </span>
            <span className="stat-label">Average Score</span>
          </div>
        </div>

        {/* Detailed Results Grid */}
        <div ref={resultsRef} className="results-section">
          <h3>Detailed Analysis</h3>
          <p className="section-description">Click on any candidate name to scroll back to the chart above.</p>
          <div className="results-grid">
            {sortedRankings?.map((ranking: any, index: number) => (
              <div
                key={index}
                ref={(el) => { candidateRefs.current[ranking.resumeFilename] = el; }}
                className={`result-card ${selectedCandidate?.resumeFilename === ranking.resumeFilename ? 'selected' : ''}`}
              >
                <div className="card-header">
                  <h3
                    className="candidate-name clickable"
                    onClick={() => handleCandidateNameClick(ranking)}
                  >
                    {ranking.resumeFilename.replace(/\.[^/.]+$/, '')}
                  </h3>
                  <div className="score-badge">
                    <span className="score">{ranking.score.toFixed(1)}%</span>
                    <span className="rank">#{ranking.rank}</span>
                  </div>
                </div>

                <div className="card-content">
                  <div className="strengths-section">
                    <h4>💪 Strengths</h4>
                    <ul>
                      {ranking.strengths?.slice(0, 3).map((strength: string, i: number) => (
                        <li key={i}>{strength}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="improvements-section">
                    <h4>🎯 Areas for Improvement</h4>
                    <ul>
                      {ranking.improvements?.slice(0, 3).map((improvement: string, i: number) => (
                        <li key={i}>{improvement}</li>
                      ))}
                    </ul>
                  </div>

                  {ranking.explanation && (
                    <div className="explanation-section">
                      <h4>📝 Analysis</h4>
                      <p>{ranking.explanation}</p>
                    </div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Summary Section */}
        {results.summary && (
          <div className="results-summary">
            <h3>📋 Summary</h3>
            <p>{results.summary}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;